import React from 'react';

import { State as ParticipantState } from '@wix/ambassador-challenges-v1-participant/types';
import {
  MemberChallenge,
  ParticipantState as ParticipantMemberState,
} from '@wix/ambassador-challenges-v1-challenge/types';
import { ChallengeImage } from './ChallengeImage';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { ChallengeBadge } from '../../../ChallengesList/Badge';
import { BadgePriority as BADGE_PRIORITY } from '@wix/wix-ui-tpa/cssVars';
import { getBadgeProps } from './getBadgeProps';
import { ChallengeInfo } from './ChallengeInfo';
import { Chevron } from '../../../../components-shared/Accordion/Chevron';
import { isChallengePaidWithoutParticipation } from './isChallengePaidWithoutparticipation';
import { LeaveChallengeButton } from './LeaveChallengeButton';
import { GoToChallenge } from './GoToChallenge';
import { ViewCertificate } from '../../../../components-shared/ViewCertificate';
import { Box } from '../../../../components-shared/Box';
import { getCertificateTrigger } from '../../../../selectors/rewards/rewardsSelectors';
import { FCWithChildren } from '@wix/challenges-web-library';
import { useMemberPaidPlans } from '../../../../contexts/storage-contexts/PaidPlans';
import { st, classes } from './ChallengeItem.st.css';

export interface IChallengeItem {
  item: MemberChallenge;
  onChallengeRemove(challengeId: string): void;
  className?: string;
}

export const ChallengeItem: FCWithChildren<IChallengeItem> = (props) => {
  const { item, onChallengeRemove, className } = props;
  const { t } = useTranslation();
  const { isMobile, isRTL } = useEnvironment();
  const [expandedInfo, setExpandedInfo] = React.useState<boolean>(false);
  const ppCtx = useMemberPaidPlans();
  const isInvited =
    (item.summary?.participation?.state as any) === ParticipantState.INVITED;
  const isRequested =
    (item.summary?.participation?.state as any) ===
    ParticipantState.JOIN_REQUESTED;

  const showCertificateBtn = React.useMemo(() => {
    const certificateTrigger = getCertificateTrigger(
      item.challenge.settings.rewards,
    );
    const isCompleted =
      item?.summary?.participation?.state === ParticipantMemberState.COMPLETED;

    return certificateTrigger && isCompleted;
  }, [item]);

  const BadgeComponent = (
    <ChallengeBadge
      className={classes.badge}
      summary={item.summary}
      badgeSkin={BADGE_PRIORITY.default}
      getBadgeStrategy={getBadgeProps}
      badgeText={
        isChallengePaidWithoutParticipation(item, ppCtx.userPaidPlans) &&
        isInvited &&
        t('members-area.challenge-badge.payment-required')
      }
    />
  );

  const BaseCTAsComponent = (
    <Box gap={3} direction={isMobile ? 'column' : null}>
      {showCertificateBtn && (
        <ViewCertificate
          certificateId={item?.summary?.participation?.participantId}
          programId={item.challenge.id}
        />
      )}
      <GoToChallenge challenge={item} />
    </Box>
  );

  return (
    <section
      className={st(
        classes.root,
        {
          mobile: isMobile,
          rtl: isRTL,
        },
        className,
      )}
    >
      <ChallengeImage challenge={item.challenge} />

      <div className={classes.description}>
        <div className={classes.descriptionRow}>
          <div className={classes.metadata}>
            <h3 className={classes.challengeName}>
              {item.challenge.settings.description.title}
            </h3>

            {isMobile ? BadgeComponent : null}

            {isChallengePaidWithoutParticipation(item, ppCtx.userPaidPlans) &&
              isInvited && (
                <p className={classes.joinInfo}>
                  {t('members-area.challenge-join-description.invited')}
                </p>
              )}

            {isChallengePaidWithoutParticipation(item, ppCtx.userPaidPlans) &&
              !isInvited &&
              !isRequested && (
                <p className={classes.joinInfo}>
                  {t('members-area.challenge-join-description.payment-started')}
                </p>
              )}

            {isMobile ? BaseCTAsComponent : null}
          </div>

          {!isMobile && (
            <>
              {BadgeComponent}
              {BaseCTAsComponent}
            </>
          )}

          <button
            aria-label={t(
              expandedInfo
                ? 'members-area.collapse-section'
                : 'members-area.expand-section',
            )}
            className={`${classes.chevron} ${
              expandedInfo ? classes.chevronExpanded : ''
            }`}
            onClick={() => {
              setExpandedInfo(!expandedInfo);
            }}
          >
            <Chevron />
          </button>
        </div>

        {expandedInfo ? (
          <div className={classes.descriptionRow}>
            <div>
              <ChallengeInfo challenge={item} />

              <LeaveChallengeButton
                challenge={item}
                onChallengeRemove={onChallengeRemove}
              />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};
